import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Table,
  Tag,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  message,
  Card,
  Tooltip,
  Checkbox,
  Alert,
  Tabs
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  ReloadOutlined,
  ArrowRightOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { exportToExcel, exportMultipleSheets } from '../../utils/excelExport';
import petService from '../../services/petService';

const { TextArea } = Input;
const { Option } = Select;

const CategorySuggestionList = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('categories');

  // Category suggestions state
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [successInfo, setSuccessInfo] = useState(null);
  const [form] = Form.useForm();

  // Breed suggestions state
  const [breedSuggestions, setBreedSuggestions] = useState([]);
  const [breedLoading, setBreedLoading] = useState(false);
  const [breedStatusFilter, setBreedStatusFilter] = useState('');
  const [breedDetailModalVisible, setBreedDetailModalVisible] = useState(false);
  const [selectedBreedSuggestion, setSelectedBreedSuggestion] = useState(null);
  const [breedUpdating, setBreedUpdating] = useState(false);
  const [breedSuccessInfo, setBreedSuccessInfo] = useState(null);
  const [breedForm] = Form.useForm();

  useEffect(() => {
    fetchSuggestions();
  }, [statusFilter]);

  useEffect(() => {
    fetchBreedSuggestions();
  }, [breedStatusFilter]);

  // Category suggestion functions
  const fetchSuggestions = async () => {
    setLoading(true);
    const params = {};
    if (statusFilter) {
      params.status = statusFilter;
    }
    const result = await petService.fetchAllSuggestions(params);
    if (result.success) {
      setSuggestions(result.data.results || result.data);
    } else {
      message.error(result.error);
    }
    setLoading(false);
  };

  // Breed suggestion functions
  const fetchBreedSuggestions = async () => {
    setBreedLoading(true);
    const params = {};
    if (breedStatusFilter) {
      params.status = breedStatusFilter;
    }
    const result = await petService.fetchAllBreedSuggestions(params);
    if (result.success) {
      setBreedSuggestions(result.data.results || result.data);
    } else {
      message.error(result.error);
    }
    setBreedLoading(false);
  };

  const handleExportCategories = () => {
    const exportColumns = [
      { title: 'ID', dataIndex: 'id' },
      { title: 'Suggested Name', dataIndex: 'suggested_name' },
      { title: 'Description', dataIndex: 'description' },
      { title: 'Submitted By', dataIndex: 'user_email' },
      { title: 'Status', dataIndex: 'status' },
      { title: 'Admin Notes', dataIndex: 'admin_notes' },
      {
        title: 'Submitted',
        dataIndex: 'created_at',
        exportRender: (date) => date ? new Date(date).toLocaleDateString() : ''
      },
      {
        title: 'Reviewed',
        dataIndex: 'reviewed_at',
        exportRender: (date) => date ? new Date(date).toLocaleDateString() : ''
      }
    ];
    exportToExcel(suggestions, exportColumns, 'category_suggestions', t('admin.categorySuggestions.categorySuggestions'));
    message.success(t('admin.categorySuggestions.exportCategorySuccess'));
  };

  const handleExportBreeds = () => {
    const exportColumns = [
      { title: 'ID', dataIndex: 'id' },
      { title: 'Suggested Breed', dataIndex: 'suggested_name' },
      { title: 'Animal Category', dataIndex: 'animal_category_name' },
      { title: 'Description', dataIndex: 'description' },
      { title: 'Submitted By', dataIndex: 'user_email' },
      { title: 'Status', dataIndex: 'status' },
      { title: 'Admin Notes', dataIndex: 'admin_notes' },
      {
        title: 'Submitted',
        dataIndex: 'created_at',
        exportRender: (date) => date ? new Date(date).toLocaleDateString() : ''
      },
      {
        title: 'Reviewed',
        dataIndex: 'reviewed_at',
        exportRender: (date) => date ? new Date(date).toLocaleDateString() : ''
      }
    ];
    exportToExcel(breedSuggestions, exportColumns, 'breed_suggestions', t('admin.categorySuggestions.breedSuggestions'));
    message.success(t('admin.categorySuggestions.exportBreedSuccess'));
  };

  const handleExportAll = () => {
    const categoryColumns = [
      { title: 'ID', dataIndex: 'id' },
      { title: 'Suggested Name', dataIndex: 'suggested_name' },
      { title: 'Description', dataIndex: 'description' },
      { title: 'Submitted By', dataIndex: 'user_email' },
      { title: 'Status', dataIndex: 'status' },
      { title: 'Admin Notes', dataIndex: 'admin_notes' },
      {
        title: 'Submitted',
        dataIndex: 'created_at',
        exportRender: (date) => date ? new Date(date).toLocaleDateString() : ''
      }
    ];
    const breedColumns = [
      { title: 'ID', dataIndex: 'id' },
      { title: 'Suggested Breed', dataIndex: 'suggested_name' },
      { title: 'Animal Category', dataIndex: 'animal_category_name' },
      { title: 'Description', dataIndex: 'description' },
      { title: 'Submitted By', dataIndex: 'user_email' },
      { title: 'Status', dataIndex: 'status' },
      { title: 'Admin Notes', dataIndex: 'admin_notes' },
      {
        title: 'Submitted',
        dataIndex: 'created_at',
        exportRender: (date) => date ? new Date(date).toLocaleDateString() : ''
      }
    ];
    exportMultipleSheets([
      { data: suggestions, columns: categoryColumns, sheetName: t('admin.categorySuggestions.categorySuggestions') },
      { data: breedSuggestions, columns: breedColumns, sheetName: t('admin.categorySuggestions.breedSuggestions') }
    ], 'pet_suggestions');
    message.success(t('admin.categorySuggestions.exportAllSuccess'));
  };

  const getStatusColor = (status) => {
    const colors = {
      PENDING: 'orange',
      REVIEWED: 'blue',
      APPROVED: 'green',
      REJECTED: 'red'
    };
    return colors[status] || 'default';
  };

  const openDetailModal = (suggestion) => {
    setSelectedSuggestion(suggestion);
    form.setFieldsValue({
      status: suggestion.status,
      admin_notes: suggestion.admin_notes || '',
      create_category: false
    });
    setDetailModalVisible(true);
  };

  const openBreedDetailModal = (suggestion) => {
    setSelectedBreedSuggestion(suggestion);
    breedForm.setFieldsValue({
      status: suggestion.status,
      admin_notes: suggestion.admin_notes || '',
      create_breed: false
    });
    setBreedDetailModalVisible(true);
  };

  const handleUpdate = async (values) => {
    if (!selectedSuggestion) return;

    setUpdating(true);
    const result = await petService.updateSuggestion(selectedSuggestion.id, {
      status: values.status,
      admin_notes: values.admin_notes,
      create_category: values.create_category
    });

    setUpdating(false);

    if (result.success) {
      message.success(t('admin.categorySuggestions.updateSuccess'));
      if (values.create_category && values.status === 'APPROVED' && result.data.category_created) {
        setSuccessInfo({
          categoryName: result.data.created_category_name,
          categoryId: result.data.created_category_id
        });
      }
      setDetailModalVisible(false);
      fetchSuggestions();
    } else {
      message.error(result.error);
    }
  };

  const handleBreedUpdate = async (values) => {
    if (!selectedBreedSuggestion) return;

    setBreedUpdating(true);
    const result = await petService.updateBreedSuggestion(selectedBreedSuggestion.id, {
      status: values.status,
      admin_notes: values.admin_notes,
      create_breed: values.create_breed
    });

    setBreedUpdating(false);

    if (result.success) {
      message.success(t('admin.categorySuggestions.breedUpdateSuccess'));
      if (values.create_breed && values.status === 'APPROVED' && result.data.breed_created) {
        setBreedSuccessInfo({
          breedName: result.data.created_breed_name,
          breedId: result.data.created_breed_id
        });
      }
      setBreedDetailModalVisible(false);
      fetchBreedSuggestions();
    } else {
      message.error(result.error);
    }
  };

  const quickAction = async (suggestion, newStatus) => {
    const result = await petService.updateSuggestion(suggestion.id, {
      status: newStatus,
      create_category: newStatus === 'APPROVED'
    });

    if (result.success) {
      message.success(newStatus === 'APPROVED' ? t('admin.categorySuggestions.approved') : t('admin.categorySuggestions.rejected'));
      if (newStatus === 'APPROVED') {
        if (result.data.category_created) {
          setSuccessInfo({
            categoryName: result.data.created_category_name,
            categoryId: result.data.created_category_id
          });
        } else if (result.data.category_activated) {
          message.success(t('admin.categorySuggestions.categoryActivated', { name: result.data.created_category_name }));
        }
      }
      fetchSuggestions();
    } else {
      message.error(result.error);
    }
  };

  const breedQuickAction = async (suggestion, newStatus) => {
    const result = await petService.updateBreedSuggestion(suggestion.id, {
      status: newStatus,
      create_breed: newStatus === 'APPROVED'
    });

    if (result.success) {
      message.success(newStatus === 'APPROVED' ? t('admin.categorySuggestions.breedApproved') : t('admin.categorySuggestions.breedRejected'));
      if (newStatus === 'APPROVED') {
        if (result.data.breed_created) {
          setBreedSuccessInfo({
            breedName: result.data.created_breed_name,
            breedId: result.data.created_breed_id
          });
        } else if (result.data.breed_activated) {
          message.success(t('admin.categorySuggestions.breedActivated', { name: result.data.created_breed_name }));
        }
      }
      fetchBreedSuggestions();
    } else {
      message.error(result.error);
    }
  };

  const categoryColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60
    },
    {
      title: t('admin.categorySuggestions.suggestedName'),
      dataIndex: 'suggested_name',
      key: 'suggested_name',
      render: (text) => <strong>{text}</strong>
    },
    {
      title: t('common.description'),
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text) => text || <span style={{ color: '#999' }}>{t('admin.categorySuggestions.noDescription')}</span>
    },
    {
      title: t('admin.categorySuggestions.submittedBy'),
      dataIndex: 'user_email',
      key: 'user_email'
    },
    {
      title: t('common.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)}>{t(`admin.categorySuggestions.statuses.${status.toLowerCase()}`)}</Tag>
      )
    },
    {
      title: t('admin.categorySuggestions.submitted'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleDateString()
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space>
          <Tooltip title={t('admin.categorySuggestions.viewDetails')}>
            <Button
              icon={<EyeOutlined />}
              size="small"
              onClick={() => openDetailModal(record)}
            />
          </Tooltip>
          {record.status === 'PENDING' && (
            <>
              <Tooltip title={t('admin.categorySuggestions.approveAndCreate')}>
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  size="small"
                  onClick={() => quickAction(record, 'APPROVED')}
                />
              </Tooltip>
              <Tooltip title={t('admin.categorySuggestions.reject')}>
                <Button
                  danger
                  icon={<CloseCircleOutlined />}
                  size="small"
                  onClick={() => quickAction(record, 'REJECTED')}
                />
              </Tooltip>
            </>
          )}
        </Space>
      )
    }
  ];

  const breedColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60
    },
    {
      title: t('admin.categorySuggestions.suggestedBreed'),
      dataIndex: 'suggested_name',
      key: 'suggested_name',
      render: (text) => <strong>{text}</strong>
    },
    {
      title: t('admin.categorySuggestions.animalCategory'),
      dataIndex: 'animal_category_name',
      key: 'animal_category_name',
      render: (text) => <Tag color="blue">{text}</Tag>
    },
    {
      title: t('common.description'),
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text) => text || <span style={{ color: '#999' }}>{t('admin.categorySuggestions.noDescription')}</span>
    },
    {
      title: t('admin.categorySuggestions.submittedBy'),
      dataIndex: 'user_email',
      key: 'user_email'
    },
    {
      title: t('common.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)}>{t(`admin.categorySuggestions.statuses.${status.toLowerCase()}`)}</Tag>
      )
    },
    {
      title: t('admin.categorySuggestions.submitted'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleDateString()
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space>
          <Tooltip title={t('admin.categorySuggestions.viewDetails')}>
            <Button
              icon={<EyeOutlined />}
              size="small"
              onClick={() => openBreedDetailModal(record)}
            />
          </Tooltip>
          {record.status === 'PENDING' && (
            <>
              <Tooltip title={t('admin.categorySuggestions.approveAndCreateBreed')}>
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  size="small"
                  onClick={() => breedQuickAction(record, 'APPROVED')}
                />
              </Tooltip>
              <Tooltip title={t('admin.categorySuggestions.reject')}>
                <Button
                  danger
                  icon={<CloseCircleOutlined />}
                  size="small"
                  onClick={() => breedQuickAction(record, 'REJECTED')}
                />
              </Tooltip>
            </>
          )}
        </Space>
      )
    }
  ];

  const tabItems = [
    {
      key: 'categories',
      label: t('admin.categorySuggestions.animalCategoriesTab', { count: suggestions.length }),
      children: (
        <>
          {successInfo && (
            <Alert
              message={t('admin.categorySuggestions.categoryCreated', { name: successInfo.categoryName })}
              description={
                <Space>
                  <span>{t('admin.categorySuggestions.categoryAvailable')}</span>
                  <Button
                    type="primary"
                    size="small"
                    icon={<ArrowRightOutlined />}
                    onClick={() => navigate('/admin/animal-categories')}
                  >
                    {t('admin.categorySuggestions.viewAnimalCategories')}
                  </Button>
                </Space>
              }
              type="success"
              showIcon
              closable
              onClose={() => setSuccessInfo(null)}
              style={{ marginBottom: 16 }}
            />
          )}
          <Space style={{ marginBottom: 16 }}>
            <Select
              placeholder={t('admin.categorySuggestions.filterByStatus')}
              allowClear
              style={{ width: 150 }}
              value={statusFilter || undefined}
              onChange={(value) => setStatusFilter(value || '')}
            >
              <Option value="PENDING">{t('admin.categorySuggestions.statuses.pending')}</Option>
              <Option value="REVIEWED">{t('admin.categorySuggestions.statuses.reviewed')}</Option>
              <Option value="APPROVED">{t('admin.categorySuggestions.statuses.approved')}</Option>
              <Option value="REJECTED">{t('admin.categorySuggestions.statuses.rejected')}</Option>
            </Select>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchSuggestions}
            >
              {t('common.refresh')}
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExportCategories}
              disabled={!suggestions.length}
            >
              {t('admin.exportExcel')}
            </Button>
          </Space>
          <p style={{ marginBottom: 16, color: '#666' }}>
            {t('admin.categorySuggestions.categoryDescription')}
          </p>
          <Table
            dataSource={suggestions}
            columns={categoryColumns}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 10 }}
          />
        </>
      )
    },
    {
      key: 'breeds',
      label: t('admin.categorySuggestions.breedsTab', { count: breedSuggestions.length }),
      children: (
        <>
          {breedSuccessInfo && (
            <Alert
              message={t('admin.categorySuggestions.breedCreated', { name: breedSuccessInfo.breedName })}
              description={t('admin.categorySuggestions.breedAvailable')}
              type="success"
              showIcon
              closable
              onClose={() => setBreedSuccessInfo(null)}
              style={{ marginBottom: 16 }}
            />
          )}
          <Space style={{ marginBottom: 16 }}>
            <Select
              placeholder={t('admin.categorySuggestions.filterByStatus')}
              allowClear
              style={{ width: 150 }}
              value={breedStatusFilter || undefined}
              onChange={(value) => setBreedStatusFilter(value || '')}
            >
              <Option value="PENDING">{t('admin.categorySuggestions.statuses.pending')}</Option>
              <Option value="REVIEWED">{t('admin.categorySuggestions.statuses.reviewed')}</Option>
              <Option value="APPROVED">{t('admin.categorySuggestions.statuses.approved')}</Option>
              <Option value="REJECTED">{t('admin.categorySuggestions.statuses.rejected')}</Option>
            </Select>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchBreedSuggestions}
            >
              {t('common.refresh')}
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExportBreeds}
              disabled={!breedSuggestions.length}
            >
              {t('admin.exportExcel')}
            </Button>
          </Space>
          <p style={{ marginBottom: 16, color: '#666' }}>
            {t('admin.categorySuggestions.breedDescription')}
          </p>
          <Table
            dataSource={breedSuggestions}
            columns={breedColumns}
            rowKey="id"
            loading={breedLoading}
            pagination={{ pageSize: 10 }}
          />
        </>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={t('admin.categorySuggestions.title')}
        extra={
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExportAll}
            disabled={!suggestions.length && !breedSuggestions.length}
          >
            {t('admin.categorySuggestions.exportAll')}
          </Button>
        }
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
        />
      </Card>

      {/* Category Detail/Edit Modal */}
      <Modal
        title={t('admin.categorySuggestions.suggestionTitle', { name: selectedSuggestion?.suggested_name })}
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={600}
      >
        {selectedSuggestion && (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleUpdate}
          >
            <div style={{ marginBottom: 16, padding: 16, background: '#f5f5f5', borderRadius: 8 }}>
              <p><strong>{t('admin.categorySuggestions.suggestedName')}:</strong> {selectedSuggestion.suggested_name}</p>
              <p><strong>{t('common.description')}:</strong> {selectedSuggestion.description || t('admin.categorySuggestions.noDescriptionProvided')}</p>
              <p><strong>{t('admin.categorySuggestions.submittedBy')}:</strong> {selectedSuggestion.user_email}</p>
              <p><strong>{t('admin.categorySuggestions.submittedOn')}:</strong> {new Date(selectedSuggestion.created_at).toLocaleString()}</p>
              {selectedSuggestion.reviewed_at && (
                <p><strong>{t('admin.categorySuggestions.reviewedOn')}:</strong> {new Date(selectedSuggestion.reviewed_at).toLocaleString()}</p>
              )}
            </div>

            <Form.Item
              label={t('common.status')}
              name="status"
              rules={[{ required: true, message: t('admin.categorySuggestions.selectStatus') }]}
            >
              <Select size="large">
                <Option value="PENDING">{t('admin.categorySuggestions.statuses.pending')}</Option>
                <Option value="REVIEWED">{t('admin.categorySuggestions.statuses.reviewed')}</Option>
                <Option value="APPROVED">{t('admin.categorySuggestions.statuses.approved')}</Option>
                <Option value="REJECTED">{t('admin.categorySuggestions.statuses.rejected')}</Option>
              </Select>
            </Form.Item>

            <Form.Item
              label={t('admin.categorySuggestions.adminNotes')}
              name="admin_notes"
              help={t('admin.categorySuggestions.adminNotesHelp')}
            >
              <TextArea rows={3} placeholder={t('admin.categorySuggestions.addNotesPlaceholder')} />
            </Form.Item>

            <Form.Item
              name="create_category"
              valuePropName="checked"
            >
              <Checkbox>
                {t('admin.categorySuggestions.createCategoryOnApprove')}
              </Checkbox>
            </Form.Item>

            <Form.Item>
              <Space>
                <Button onClick={() => setDetailModalVisible(false)}>
                  {t('common.cancel')}
                </Button>
                <Button type="primary" htmlType="submit" loading={updating}>
                  {t('admin.categorySuggestions.updateSuggestion')}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* Breed Detail/Edit Modal */}
      <Modal
        title={t('admin.categorySuggestions.breedSuggestionTitle', { name: selectedBreedSuggestion?.suggested_name })}
        open={breedDetailModalVisible}
        onCancel={() => setBreedDetailModalVisible(false)}
        footer={null}
        width={600}
      >
        {selectedBreedSuggestion && (
          <Form
            form={breedForm}
            layout="vertical"
            onFinish={handleBreedUpdate}
          >
            <div style={{ marginBottom: 16, padding: 16, background: '#f5f5f5', borderRadius: 8 }}>
              <p><strong>{t('admin.categorySuggestions.suggestedBreed')}:</strong> {selectedBreedSuggestion.suggested_name}</p>
              <p><strong>{t('admin.categorySuggestions.animalCategory')}:</strong> <Tag color="blue">{selectedBreedSuggestion.animal_category_name}</Tag></p>
              <p><strong>{t('common.description')}:</strong> {selectedBreedSuggestion.description || t('admin.categorySuggestions.noDescriptionProvided')}</p>
              <p><strong>{t('admin.categorySuggestions.submittedBy')}:</strong> {selectedBreedSuggestion.user_email}</p>
              <p><strong>{t('admin.categorySuggestions.submittedOn')}:</strong> {new Date(selectedBreedSuggestion.created_at).toLocaleString()}</p>
              {selectedBreedSuggestion.reviewed_at && (
                <p><strong>{t('admin.categorySuggestions.reviewedOn')}:</strong> {new Date(selectedBreedSuggestion.reviewed_at).toLocaleString()}</p>
              )}
            </div>

            <Form.Item
              label={t('common.status')}
              name="status"
              rules={[{ required: true, message: t('admin.categorySuggestions.selectStatus') }]}
            >
              <Select size="large">
                <Option value="PENDING">{t('admin.categorySuggestions.statuses.pending')}</Option>
                <Option value="REVIEWED">{t('admin.categorySuggestions.statuses.reviewed')}</Option>
                <Option value="APPROVED">{t('admin.categorySuggestions.statuses.approved')}</Option>
                <Option value="REJECTED">{t('admin.categorySuggestions.statuses.rejected')}</Option>
              </Select>
            </Form.Item>

            <Form.Item
              label={t('admin.categorySuggestions.adminNotes')}
              name="admin_notes"
              help={t('admin.categorySuggestions.adminNotesHelp')}
            >
              <TextArea rows={3} placeholder={t('admin.categorySuggestions.addBreedNotesPlaceholder')} />
            </Form.Item>

            <Form.Item
              name="create_breed"
              valuePropName="checked"
            >
              <Checkbox>
                {t('admin.categorySuggestions.createBreedOnApprove')}
              </Checkbox>
            </Form.Item>

            <Form.Item>
              <Space>
                <Button onClick={() => setBreedDetailModalVisible(false)}>
                  {t('common.cancel')}
                </Button>
                <Button type="primary" htmlType="submit" loading={breedUpdating}>
                  {t('admin.categorySuggestions.updateSuggestion')}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
});

export default CategorySuggestionList;
